import {
  ArrowUp,
  Camera,
  FileImage,
  Mic,
  Plus,
  Square,
} from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { ReasoningEffort } from "../lib/model-capabilities";
import { IconButton } from "./IconButton";

type ComposerProps = {
  value: string;
  isStreaming: boolean;
  disabled?: boolean;
  effort: ReasoningEffort;
  effortOptions: ReasoningEffort[];
  attachmentStatus?: string;
  isEmptyConversation: boolean;
  suggestions: string[];
  onChange: (value: string) => void;
  onEffortChange: (value: ReasoningEffort) => void;
  onAttachFiles: (files: FileList) => void;
  onSend: () => void;
  onStop: () => void;
  onSelectSuggestion: (value: string) => void;
};

export function Composer({
  value,
  isStreaming,
  disabled = false,
  effort,
  effortOptions,
  attachmentStatus,
  isEmptyConversation,
  suggestions,
  onChange,
  onEffortChange,
  onAttachFiles,
  onSend,
  onStop,
  onSelectSuggestion,
}: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const toolsButtonAreaRef = useRef<HTMLDivElement>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`;
  }, [value]);

  useEffect(() => {
    if (!toolsOpen) return;
    const closeWhenOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (toolsButtonAreaRef.current?.contains(target) || toolsMenuRef.current?.contains(target)) return;
      setToolsOpen(false);
    };
    document.addEventListener("pointerdown", closeWhenOutside);
    return () => document.removeEventListener("pointerdown", closeWhenOutside);
  }, [toolsOpen]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSend();
    }
  };

  const startDictation = () => {
    type SpeechRecognitionConstructor = new () => {
      lang: string;
      interimResults: boolean;
      continuous: boolean;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    };
    const recognitionConstructor = (
      window as typeof window & {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
      }
    ).SpeechRecognition ?? (
      window as typeof window & { webkitSpeechRecognition?: SpeechRecognitionConstructor }
    ).webkitSpeechRecognition;

    if (!recognitionConstructor) {
      textareaRef.current?.focus();
      return;
    }

    const recognition = new recognitionConstructor();
    recognition.lang = "ko-KR";
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map((result) => result[0]?.transcript ?? "").join(" ").trim();
      if (transcript) onChange([value.trim(), transcript].filter(Boolean).join(" "));
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    setListening(true);
    recognition.start();
  };

  return (
    <>
      <div className="composer-wrap">
        {isEmptyConversation && (
          <div className="suggestion-row" aria-label="추천 질문">
            {suggestions.slice(0, 3).map((suggestion) => (
              <button type="button" key={suggestion} onClick={() => onSelectSuggestion(suggestion)}>{suggestion}</button>
            ))}
          </div>
        )}
        {attachmentStatus && <div className="attachment-status" role="status">{attachmentStatus}</div>}
        <form className="composer liquid-control" onSubmit={(event) => { event.preventDefault(); onSend(); }}>
          <div className="composer__toolbar-start" ref={toolsButtonAreaRef}>
            <IconButton label="파일 및 도구 추가" onClick={() => setToolsOpen(true)} className="composer__plus">
              <Plus size={21} />
            </IconButton>
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={listening ? "듣고 있어요…" : "Hermes에게 물어보기"}
            aria-label="메시지"
          />
          <div className="composer__toolbar-end">
            <select
              className="effort-select"
              value={effortOptions.length ? effort : ""}
              onChange={(event) => {
                const value = event.currentTarget.value as ReasoningEffort;
                onEffortChange(value);
              }}
              aria-label="추론 강도"
              disabled={disabled || !effortOptions.length}
            >
              {!effortOptions.length && <option value="">추론 미지원</option>}
              {effortOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            {isStreaming ? (
              <IconButton label="응답 중지" variant="accent" onClick={onStop} className="composer__action"><Square size={13} fill="currentColor" /></IconButton>
            ) : value.trim() ? (
              <IconButton label="메시지 보내기" variant="accent" type="submit" className="composer__action" disabled={disabled}><ArrowUp size={20} strokeWidth={2.5} /></IconButton>
            ) : (
              <IconButton label={listening ? "받아쓰기 중지" : "받아쓰기 시작"} variant="accent" onClick={startDictation} className={`composer__action ${listening ? "is-listening" : ""}`} disabled={disabled}><Mic size={20} /></IconButton>
            )}
          </div>
        </form>

        {toolsOpen && (
          <div ref={toolsMenuRef} className="composer-menu composer-menu--tools liquid-control" role="menu">
            <label role="menuitem">
              <FileImage size={19} /><span>사진 추가</span>
              <input type="file" hidden multiple accept="image/*" onChange={(event) => { if (event.target.files?.length) onAttachFiles(event.target.files); setToolsOpen(false); event.target.value = ""; }} />
            </label>
            <button type="button" role="menuitem" onClick={() => cameraInputRef.current?.click()}><Camera size={19} /><span>카메라</span></button>
            <input ref={cameraInputRef} type="file" hidden accept="image/*" capture="environment" onChange={(event) => { if (event.target.files?.length) onAttachFiles(event.target.files); setToolsOpen(false); event.target.value = ""; }} />
          </div>
        )}

        <p className="composer-note">Hermes는 실수할 수 있습니다. 중요한 정보는 확인하세요.</p>
      </div>

    </>
  );
}
